/**
 * เงาจริงบนพื้น (รอบ 20) — ShadowGenerator จากไฟ "sun" ครอบเฉพาะรอบตัวละคร เลื่อนตามกล้องทุกเฟรม
 *
 * ใครทอดเงา: mesh ที่บรรพบุรุษมี `metadata.castShadow` (ตัวละคร มอน — main.ts ตั้งที่ตัวรับคลิก)
 * และ mesh ของตกแต่งที่รวมแล้ว ("deco") · ข้ามของล่องหน (visibility 0 = ตัวรับคลิก) และวงเงาปลอม ("sh")
 * คุณภาพ low = ไม่สร้างเลย (มือถือ) · ต้อง import side-effect ของ shadow ไม่งั้นพังเงียบ (กับดัก Babylon ใน AGENTS)
 */
import type { Scene } from "@babylonjs/core/scene";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import type { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { Vector3 } from "@babylonjs/core/Maths/math";
import { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import "@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent";

/** ครึ่งความกว้างของพื้นที่ที่มีเงารอบตัวละคร (หน่วยโลก) — กล้องมุมสูงเห็นราว ±25 */
export const SHADOW_REACH = 34;
const SKIP = new Set(["sh", "elemRing", "sky-dome", "scatter", "ground"]);

/** ทอดเงาไหม (pure — มีเทสต์) */
export function castsShadow(m: Pick<AbstractMesh, "name" | "visibility" | "isEnabled" | "parent" | "metadata"> & { isVisible?: boolean }): boolean {
  if (SKIP.has(m.name) || m.visibility === 0 || m.isVisible === false || !m.isEnabled()) return false;
  if (m.name === "deco") return true;
  let n: { metadata?: unknown; parent?: unknown } | null = m;
  for (let i = 0; n && i < 8; i++) {
    if ((n.metadata as { castShadow?: boolean } | undefined)?.castShadow) return true;
    n = (n.parent as typeof n) ?? null;
  }
  return false;
}

export interface Shadows {
  dispose(): void;
}

export function setupShadows(scene: Scene, sun: DirectionalLight, follow: () => Vector3): Shadows {
  const gen = new ShadowGenerator(1024, sun);
  gen.usePercentageCloserFiltering = true;
  gen.filteringQuality = ShadowGenerator.QUALITY_MEDIUM;
  gen.bias = 0.0015;
  gen.normalBias = 0.02;
  gen.darkness = 0.35;
  gen.transparencyShadow = false;
  const map = gen.getShadowMap()!;
  map.renderListPredicate = (m) => castsShadow(m as AbstractMesh);
  sun.autoUpdateExtends = false;
  sun.autoCalcShadowZBounds = false;
  sun.orthoLeft = -SHADOW_REACH;
  sun.orthoRight = SHADOW_REACH;
  sun.orthoTop = SHADOW_REACH;
  sun.orthoBottom = -SHADOW_REACH;
  sun.shadowMinZ = 1;
  sun.shadowMaxZ = 160;
  const dir = sun.direction.normalizeToNew();
  const obs = scene.onBeforeRenderObservable.add(() => {
    const t = follow();
    sun.position.copyFrom(t.subtract(dir.scale(80)));
    // พื้นและของตกแต่งรับเงา (ตั้งทุกเฟรมถูกกว่าไล่หา mesh ใหม่ตอนสลับโซน)
    for (const m of scene.meshes) if ((m.name === "ground" || m.name === "deco") && !m.receiveShadows) m.receiveShadows = true;
  });
  return {
    dispose() {
      scene.onBeforeRenderObservable.remove(obs);
      gen.dispose();
    },
  };
}
